import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ImageIcon, Loader2, Plus, Search, Trash2, UploadCloud, X } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import type { Shop, ShopInput } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type Toast = { kind: 'ok' | 'err'; message: string } | null

function ToastBar({ toast }: { toast: Toast }) {
  if (!toast) return null
  return (
    <div
      className={
        'rounded-md border px-3 py-2 text-sm ' +
        (toast.kind === 'ok'
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-destructive/30 bg-destructive/10 text-destructive')
      }
    >
      {toast.message}
    </div>
  )
}

const PAGE = 40

/** Firma = rekordy sklepów o tej samej nazwie i tej samej ikonce. Każdy rekord
 * trzyma jeden link (`URL`), więc grupujemy je pod wspólną nazwą, a linki
 * dodajemy/usuwamy jako pojedyncze rekordy. */
interface ShopGroup {
  key: string
  name: string
  logoMobile: string
  links: Shop[]
}

function groupKey(name: string, logoMobile: string): string {
  return `${name} ${logoMobile}`
}

function groupShops(shops: Shop[]): ShopGroup[] {
  const map = new Map<string, ShopGroup>()
  for (const shop of shops) {
    const key = groupKey(shop.name, shop.logoMobile)
    let group = map.get(key)
    if (!group) {
      group = { key, name: shop.name, logoMobile: shop.logoMobile, links: [] }
      map.set(key, group)
    }
    group.links.push(shop)
  }
  return [...map.values()]
}

export default function Shops() {
  const queryClient = useQueryClient()
  const shops = useQuery({ queryKey: ['shops'], queryFn: api.shopsApi.list })
  const [toast, setToast] = useState<Toast>(null)
  const [query, setQuery] = useState('')
  const [limit, setLimit] = useState(PAGE)
  const [editing, setEditing] = useState<ShopGroup | null>(null)
  const [creating, setCreating] = useState(false)

  const groups = useMemo(() => groupShops(shops.data ?? []), [shops.data])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return groups
    return groups.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        g.links.some((l) => l.URL.toLowerCase().includes(q)),
    )
  }, [groups, query])

  const visible = filtered.slice(0, limit)
  const totalLinks = useMemo(
    () => (shops.data ?? []).length,
    [shops.data],
  )

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['shops'] })
  }

  return (
    <div className="flex flex-col gap-4">
      <ToastBar toast={toast} />
      <div className="flex flex-col gap-1">
        <h1 className="text-lg font-semibold">Sklepy</h1>
        <p className="text-sm text-muted-foreground">
          Katalog dopasowania logo dla buy-boxów. Firmy o tej samej nazwie i ikonce są zgrupowane —
          pod jedną nazwą trzymasz wszystkie linki (gołe domeny i linki afiliacyjne). Worker matchuje
          pozycję buy-boxa, gdy jej link <em>zawiera</em> któryś z tych fragmentów.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[260px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setLimit(PAGE)
            }}
            placeholder="Szukaj po nazwie lub linku…"
            className="px-8"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" /> Nowa firma
        </Button>
      </div>

      <div className="text-xs text-muted-foreground">
        {shops.isLoading
          ? 'Ładowanie…'
          : `${filtered.length.toLocaleString('pl-PL')} firm${
              query ? ' (po filtrze)' : ''
            } · ${totalLinks.toLocaleString('pl-PL')} linków.`}
      </div>

      <div className="flex flex-col gap-3">
        {visible.length === 0 && !shops.isLoading && (
          <Card>
            <CardContent className="py-6 text-center text-sm text-muted-foreground">
              Brak firm.
            </CardContent>
          </Card>
        )}
        {visible.map((group) => (
          <CompanyCard
            key={group.key}
            group={group}
            onEdit={() => setEditing(group)}
            onToast={setToast}
            onChanged={invalidate}
          />
        ))}
      </div>

      {visible.length < filtered.length && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => setLimit((l) => l + PAGE)}>
            Pokaż więcej ({filtered.length - visible.length})
          </Button>
        </div>
      )}

      {(creating || editing) && (
        <CompanyFormModal
          group={editing}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onToast={setToast}
          onSaved={() => {
            invalidate()
            setCreating(false)
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}

function CompanyCard({
  group,
  onEdit,
  onToast,
  onChanged,
}: {
  group: ShopGroup
  onEdit: () => void
  onToast: (t: Toast) => void
  onChanged: () => void
}) {
  const [newLink, setNewLink] = useState('')

  const addLink = useMutation({
    mutationFn: (url: string) => {
      const trimmed = url.trim()
      if (!trimmed) throw new Error('Link nie może być pusty.')
      const body: ShopInput = { name: group.name, URL: trimmed, logoMobile: group.logoMobile }
      return api.shopsApi.create(body)
    },
    onSuccess: () => {
      setNewLink('')
      onToast({ kind: 'ok', message: 'Dodano link.' })
      onChanged()
    },
    onError: (e) => onToast({ kind: 'err', message: (e as Error).message }),
  })

  const removeLink = useMutation({
    mutationFn: (id: string) => api.shopsApi.remove(id),
    onSuccess: () => {
      onToast({ kind: 'ok', message: 'Usunięto link.' })
      onChanged()
    },
    onError: (e) => onToast({ kind: 'err', message: (e as Error).message }),
  })

  const removeCompany = useMutation({
    mutationFn: () => Promise.all(group.links.map((l) => api.shopsApi.remove(l.id))),
    onSuccess: () => {
      onToast({ kind: 'ok', message: 'Usunięto firmę.' })
      onChanged()
    },
    onError: (e) => onToast({ kind: 'err', message: (e as Error).message }),
  })

  return (
    <Card className="py-0">
      <CardContent className="flex flex-col gap-3 px-4 py-3">
        <div className="flex items-center gap-3">
          <LogoThumb src={group.logoMobile} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{group.name || '—'}</div>
            <div className="text-xs text-muted-foreground">
              {group.links.length} {group.links.length === 1 ? 'link' : 'linków'}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onEdit}>
            Edytuj
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            disabled={removeCompany.isPending}
            onClick={() => {
              if (confirm(`Usunąć firmę „${group.name}” wraz z ${group.links.length} linkami?`))
                removeCompany.mutate()
            }}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>

        <ul className="flex flex-col gap-1.5 border-t pt-3">
          {group.links.map((link) => (
            <li key={link.id} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground">
                {link.URL || '—'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-destructive hover:text-destructive"
                disabled={removeLink.isPending}
                onClick={() => {
                  if (confirm(`Usunąć link „${link.URL}”?`)) removeLink.mutate(link.id)
                }}
              >
                <X className="size-4" />
              </Button>
            </li>
          ))}
        </ul>

        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            addLink.mutate(newLink)
          }}
        >
          <Input
            value={newLink}
            onChange={(e) => setNewLink(e.target.value)}
            placeholder="Dodaj link, np. mediaexpert.pl lub https://track.../aff_c?…"
            className="h-8 font-mono text-xs"
          />
          <Button type="submit" size="sm" className="h-8" disabled={addLink.isPending}>
            {addLink.isPending ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            Dodaj
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function LogoThumb({ src }: { src: string }) {
  const [errored, setErrored] = useState(false)
  if (!src || errored) {
    return (
      <div className="flex size-10 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
        <ImageIcon className="size-4" />
      </div>
    )
  }
  return (
    <img
      src={src}
      alt=""
      className="size-10 shrink-0 rounded border bg-white object-contain"
      onError={() => setErrored(true)}
    />
  )
}

function CompanyFormModal({
  group,
  onClose,
  onSaved,
  onToast,
}: {
  group: ShopGroup | null
  onClose: () => void
  onSaved: () => void
  onToast: (t: Toast) => void
}) {
  const [name, setName] = useState(group?.name ?? '')
  const [logoMobile, setLogoMobile] = useState(group?.logoMobile ?? '')
  const [firstLink, setFirstLink] = useState('')
  const [err, setErr] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      const cleanName = name.trim()
      const cleanLogo = logoMobile.trim()
      if (!cleanName) throw new Error('Nazwa nie może być pusta.')
      if (group) {
        // Edycja firmy: zaktualizuj nazwę i logo we wszystkich linkach, zachowując ich URL-e.
        await Promise.all(
          group.links.map((l) =>
            api.shopsApi.update(l.id, { name: cleanName, URL: l.URL, logoMobile: cleanLogo }),
          ),
        )
      } else {
        const body: ShopInput = { name: cleanName, URL: firstLink.trim(), logoMobile: cleanLogo }
        await api.shopsApi.create(body)
      }
    },
    onSuccess: () => {
      onToast({ kind: 'ok', message: group ? 'Zapisano firmę.' : 'Dodano firmę.' })
      onSaved()
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) setErr('Walidacja nieudana — sprawdź dane.')
      else setErr((e as Error).message)
    },
  })

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{group ? 'Edytuj firmę' : 'Nowa firma'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="shop-name">Nazwa</Label>
            <Input
              id="shop-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (err) setErr(null)
              }}
              placeholder="np. Media Expert"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Logo</Label>
            <LogoDropzone
              name={name}
              value={logoMobile}
              onChange={setLogoMobile}
              onToast={onToast}
            />
          </div>
          {group ? (
            <p className="text-xs text-muted-foreground">
              Zmiana nazwy lub logo zostanie zastosowana do wszystkich {group.links.length} linków
              tej firmy. Same linki dodajesz i usuwasz na liście firmy.
            </p>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="shop-link">Pierwszy link (opcjonalnie)</Label>
              <Input
                id="shop-link"
                value={firstLink}
                onChange={(e) => setFirstLink(e.target.value)}
                placeholder="mediaexpert.pl lub https://track.../aff_c?offer_id=…"
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">
                Kolejne linki dodasz na liście firmy po jej utworzeniu.
              </p>
            </div>
          )}
          {err && <p className="text-xs text-destructive">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : group ? 'Zapisz' : 'Dodaj'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function LogoDropzone({
  name,
  value,
  onChange,
  onToast,
}: {
  name: string
  value: string
  onChange: (url: string) => void
  onToast: (t: Toast) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const upload = useMutation({
    mutationFn: (file: File) => {
      if (!file.type.startsWith('image/')) throw new Error('To nie jest plik graficzny.')
      if (file.size > 10 * 1024 * 1024) throw new Error('Plik większy niż 10 MB.')
      return api.shopsApi.uploadLogo(file, name)
    },
    onSuccess: (res) => {
      onChange(res.url)
      onToast({ kind: 'ok', message: 'Wgrano logo.' })
    },
    onError: (e) => onToast({ kind: 'err', message: (e as Error).message }),
  })

  function handleFiles(files: FileList | null) {
    const file = files?.[0]
    if (file) upload.mutate(file)
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        handleFiles(e.dataTransfer.files)
      }}
      onClick={() => inputRef.current?.click()}
      className={
        'flex cursor-pointer items-center gap-3 rounded-md border border-dashed px-3 py-3 text-sm transition ' +
        (dragOver ? 'border-primary bg-primary/5' : 'border-input hover:bg-muted/50')
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {value ? (
        <img
          src={value}
          alt=""
          className="size-12 shrink-0 rounded border bg-white object-contain"
        />
      ) : (
        <div className="flex size-12 shrink-0 items-center justify-center rounded border bg-muted text-muted-foreground">
          <ImageIcon className="size-5" />
        </div>
      )}
      <div className="flex items-center gap-2 text-muted-foreground">
        {upload.isPending ? (
          <>
            <Loader2 className="size-4 animate-spin" /> Wgrywanie…
          </>
        ) : (
          <>
            <UploadCloud className="size-4" />
            Przeciągnij plik lub kliknij, by wybrać
          </>
        )}
      </div>
    </div>
  )
}
